/**
 * Tray choice strip for #bottom-rows-container: same inject pattern as AudioPlayer.
 * Outer shell matches #ar-instruction-text-container (12px radius, white, shadow).
 * Selected pill uses AudioPlayer .bottom-bar-box radius (8px) and primaryColor.
 */

const OUTER_RADIUS_PX = 12
const INNER_RADIUS_PX = 8

export type TrayChoiceOption = {
  id: string
  label: string
}

export type TrayChoiceSettings = {
  primaryColor?: string
  defaultSelectedIndex?: number
  onSelect?: (id: string, index: number, option: TrayChoiceOption) => void
}

export type TrayChoiceChangeDetail = {
  id: string
  index: number
  option: TrayChoiceOption
}

export default class TrayChoice extends EventTarget {
  settings: TrayChoiceSettings
  primaryColor: string
  options: TrayChoiceOption[]
  selectedIndex: number
  isInputDisabled = false
  onSelect: TrayChoiceSettings['onSelect']
  root: HTMLElement | null = null
  optionButtons: HTMLButtonElement[] = []

  constructor(settings: TrayChoiceSettings = {}) {
    super()
    this.settings = settings
    this.primaryColor = settings.primaryColor || '#da131c'
    document.documentElement.style.setProperty('--primary-color', this.primaryColor)
    this.options = []
    this.selectedIndex = settings.defaultSelectedIndex ?? -1

    this.injectCSS()
    this.injectHTML()
    this.cacheDOM()
    this.updateStyle()
    this.applyInputState()

  }

  injectCSS() {
    const styleId = 'tray-choice-style'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .tray-choice {
        position: relative;
        width: 100%;
        box-sizing: border-box;
        z-index: 100;
        pointer-events: auto;
        display: none;
      }
      .tray-choice-track {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        width: 100%;
        height: 64px;
        box-sizing: border-box;
        background-color: #ffffff;
        border-radius: ${OUTER_RADIUS_PX}px;
        padding: 6px;
        
        box-shadow: var(--spotar-ui-box-shadow);
        transition: opacity 0.2s ease, filter 0.2s ease;
      }
      .tray-choice[data-input-disabled="true"] .tray-choice-track {
        opacity: 0.55;
        filter: saturate(0.6);
      }
      .tray-choice-spacer {
        flex: 0 0 10px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: stretch;
        align-self: stretch;
        pointer-events: none;
      }
      .tray-choice-spacer::after {
        content: "";
        flex: 0 0 1.5px;
        width: 1.5px;
        min-width: 1.5px;
        margin: 2px 0;
        background-color: #eceef2;
      }
      .tray-choice-option {
        position: relative;
        flex: 1 1 0;
        min-width: 0;
        margin: 0;
        padding: 10px 8px;
        border: none;
        box-shadow: none;
        appearance: none;
        -webkit-appearance: none;
        -webkit-tap-highlight-color: transparent;
        background: transparent;
        font-family: Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 15px;
        line-height: 1.2;
        font-weight: 600;
        color: #9ca3af;
        cursor: pointer;
        border-radius: ${INNER_RADIUS_PX}px;
        transition: background-color 0.2s ease, color 0.2s ease;
      }
      .tray-choice[data-input-disabled="true"] .tray-choice-option {
        cursor: not-allowed;
      }
      .tray-choice-option.tray-choice-option-selected {
        background-color: var(--primary-color);
        color: #ffffff;
        z-index: 1;
      }
    `
    document.head.appendChild(style)
  }

  injectHTML() {
    document.getElementById('tray-choice-root')?.remove()

    const root = document.createElement('div')
    root.id = 'tray-choice-root'
    root.className = 'tray-choice'

    const track = document.createElement('div')
    track.className = 'tray-choice-track'
    track.setAttribute('role', 'listbox')
    this.fillTrack(track)

    root.appendChild(track)

    document.getElementById('bottom-rows-container')!.appendChild(root)
  }

  fillTrack(track: HTMLElement) {
    this.options.forEach((option, index) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'tray-choice-option'
      btn.setAttribute('role', 'option')
      btn.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false')
      btn.dataset.index = String(index)
      btn.dataset.id = option.id
      btn.textContent = option.label
      track.appendChild(btn)
      if (index < this.options.length - 1) {
        const spacer = document.createElement('span')
        spacer.className = 'tray-choice-spacer'
        spacer.setAttribute('aria-hidden', 'true')
        spacer.setAttribute('role', 'presentation')
        track.appendChild(spacer)
      }
    })
  }

  updateOptions(options: TrayChoiceOption[]) {
    this.options = options
    const max = Math.max(0, this.options.length - 1)
    this.selectedIndex = Math.min(this.selectedIndex, max)
    const track = this.root?.querySelector<HTMLElement>('.tray-choice-track')
    if (!track) return
    track.replaceChildren()
    this.fillTrack(track)
    this.cacheDOM()
    this.bindEvents()
    this.updateStyle()
    this.applyInputState()
  }

  cacheDOM() {
    this.root = document.getElementById('tray-choice-root')
    this.optionButtons = this.root
      ? Array.from(this.root.querySelectorAll<HTMLButtonElement>('.tray-choice-option'))
      : []
  }

  bindEvents() {
    this.optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = Number(btn.dataset.index)
        if (index === this.selectedIndex) return
        this.selectedIndex = index
        this.updateStyle()
        const option = this.options[index]
        if (this.onSelect) this.onSelect(option.id, index, option)
        this.dispatchEvent(
          new CustomEvent<TrayChoiceChangeDetail>('traychoicechange', {
            detail: { id: option.id, index, option },
          })
        )
      })
    })
  }

  updateStyle() {
    this.optionButtons.forEach((btn, i) => {
      const selected = i === this.selectedIndex
      btn.classList.toggle('tray-choice-option-selected', selected)
      btn.setAttribute('aria-selected', selected ? 'true' : 'false')
    })
  }

  /** Visual shell of the strip — the element an effect should wrap as a whole. */
  getTrackElement(): HTMLElement | null {
    return this.root?.querySelector<HTMLElement>('.tray-choice-track') ?? null
  }

  getOptionElement(optionId: string): HTMLElement | null {
    return this.optionButtons.find((button) => button.dataset.id === optionId) ?? null
  }

  getOptionElements(): HTMLElement[] {
    return [...this.optionButtons]
  }

  setInputDisabled(isDisabled: boolean) {
    this.isInputDisabled = isDisabled
    this.applyInputState()
  }

  /**
   * Mirrors `isInputDisabled` onto the DOM. Single place that owns both the look
   * (data attribute on the root) and the interactivity (button `disabled`), so a
   * rebuilt track can never keep a stale disabled appearance.
   */
  applyInputState() {
    if (!this.root) return

    const isDisabled = this.isInputDisabled
    this.root.dataset.inputDisabled = isDisabled ? 'true' : 'false'

    const track = this.getTrackElement()
    track?.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')

    this.optionButtons.forEach((button) => {
      button.disabled = isDisabled
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')
    })
  }

  setSelectedIndex(index: number) {
    if (index === -1 || (index >= 0 && index < this.options.length)) {
      this.selectedIndex = index
      this.updateStyle()
    }
  }
  show() {
    if (this.root) {
      this.root.style.display = 'block'
    }
  }
  hide() {
    if (this.root) {
      this.root.style.display = 'none'
    }
  }
}
